import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { UploadCloud, Loader2, ArrowLeft, Save, ImageIcon } from "lucide-react";
import { toast } from "sonner";

import api, { formatApiError } from "@/lib/api";
import { useContent, resolveMediaUrl } from "@/context/ContentContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const SLOTS = [
  { key: "hero", label: "Hero background", note: "Full-screen photo behind the landing headline." },
  { key: "food", label: "Food photo", note: "Featured food shot in the gallery grid." },
  { key: "dining", label: "Dining photo", note: "Used on the booking page split-screen and gallery." },
  { key: "gallery_1", label: "Gallery 1 (large)", note: "Large left cell in the gallery grid." },
  { key: "gallery_2", label: "Gallery 2", note: "Small tile in the gallery grid." },
  { key: "gallery_3", label: "Gallery 3", note: "Bottom right tile in the gallery grid." },
];

const TEXT_FIELDS = [
  { key: "hero_overline", label: "Hero overline", type: "input" },
  { key: "hero_title_line1", label: "Hero headline · line 1", type: "input" },
  { key: "hero_title_line2", label: "Hero headline · line 2", type: "input" },
  { key: "hero_subtitle", label: "Hero subtitle", type: "textarea" },
  { key: "story_title", label: "Story headline", type: "input" },
  { key: "story_paragraph_1", label: "Story paragraph 1", type: "textarea" },
  { key: "story_paragraph_2", label: "Story paragraph 2", type: "textarea" },
  { key: "visit_title", label: "Visit headline", type: "input" },
  { key: "visit_subtitle", label: "Visit subtitle", type: "textarea" },
  { key: "address", label: "Address", type: "input" },
  { key: "phone", label: "Phone", type: "input" },
  { key: "whatsapp", label: "WhatsApp", type: "input" },
  { key: "email", label: "Email", type: "input" },
  { key: "hours", label: "Hours", type: "input" },
  { key: "menu_url", label: "Menu PDF URL", type: "input" },
];

export default function AdminContentPage() {
  const { content, refresh, ready } = useContent();
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (ready) {
      const next = {};
      TEXT_FIELDS.forEach((f) => (next[f.key] = content?.[f.key] || ""));
      setDraft(next);
    }
  }, [ready, content]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content", draft);
      await refresh();
      toast.success("Details saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F6F0]" data-testid="admin-content-page">
      <header className="border-b border-[#E5E0D8] bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="text-[#8C7A6B] hover:text-[#D35400] flex items-center gap-2" data-testid="content-back">
              <ArrowLeft className="h-4 w-4" /> <span className="text-sm">Dashboard</span>
            </Link>
            <span className="overline">Site Content</span>
          </div>
          <Link to="/" target="_blank" className="text-sm text-[#8C7A6B] hover:text-[#D35400]" data-testid="content-view-site">
            View live site ↗
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        <div className="mb-8">
          <div className="overline">Manage site</div>
          <h1 className="mt-2 font-display text-4xl">Photos & details</h1>
          <p className="mt-2 text-sm text-[#8C7A6B] max-w-2xl">
            Upload photos and edit every word on the public site. Changes are live immediately (browser cache may need a refresh).
          </p>
        </div>

        <Tabs defaultValue="photos" className="space-y-8">
          <TabsList className="bg-white border border-[#E5E0D8]">
            <TabsTrigger value="photos" data-testid="content-tab-photos">Photos</TabsTrigger>
            <TabsTrigger value="details" data-testid="content-tab-details">Details</TabsTrigger>
          </TabsList>

          {/* PHOTOS */}
          <TabsContent value="photos">
            <div className="grid md:grid-cols-2 gap-6">
              {SLOTS.map((s) => (
                <PhotoSlot
                  key={s.key}
                  slot={s.key}
                  label={s.label}
                  note={s.note}
                  url={resolveMediaUrl(content?.images?.[s.key])}
                  onUploaded={refresh}
                />
              ))}
            </div>
          </TabsContent>

          {/* DETAILS */}
          <TabsContent value="details">
            <div className="bg-white border border-[#E5E0D8] rounded-lg p-8">
              <div className="grid md:grid-cols-2 gap-6">
                {TEXT_FIELDS.map((f) => (
                  <div key={f.key} className={f.type === "textarea" ? "md:col-span-2" : ""}>
                    <Label className="overline" htmlFor={f.key}>{f.label}</Label>
                    {f.type === "textarea" ? (
                      <Textarea
                        id={f.key}
                        value={draft[f.key] || ""}
                        onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                        rows={3}
                        className="mt-2 bg-white border-[#E5E0D8]"
                        data-testid={`content-field-${f.key}`}
                      />
                    ) : (
                      <Input
                        id={f.key}
                        value={draft[f.key] || ""}
                        onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                        className="mt-2 h-11 bg-white border-[#E5E0D8]"
                        data-testid={`content-field-${f.key}`}
                      />
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-8 flex justify-end">
                <Button
                  onClick={save}
                  disabled={saving}
                  className="btn-terracotta rounded-full h-11 px-8"
                  data-testid="content-save"
                >
                  {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving…</> : <><Save className="mr-2 h-4 w-4" />Save changes</>}
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function PhotoSlot({ slot, label, note, url, onUploaded }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const pick = () => inputRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image too large (max 8 MB)");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("slot", slot);
      form.append("file", file);
      await api.post("/admin/content/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${label} updated`);
      onUploaded?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="bg-white border border-[#E5E0D8] rounded-lg overflow-hidden" data-testid={`photo-slot-${slot}`}>
      <div className="relative aspect-[16/10] bg-[#F9F6F0]">
        {url ? (
          <img src={url} alt={label} className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-[#8C7A6B]">
            <ImageIcon className="h-6 w-6" />
          </div>
        )}
      </div>
      <div className="p-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[#1A2B3C] font-medium">{label}</div>
            <div className="text-xs text-[#8C7A6B] mt-1 max-w-xs">{note}</div>
          </div>
          <Button
            onClick={pick}
            disabled={uploading}
            variant="outline"
            className="rounded-full border-[#E5E0D8] h-10"
            data-testid={`photo-upload-${slot}`}
          >
            {uploading ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading…</>
            ) : (
              <><UploadCloud className="h-4 w-4 mr-2" />Replace</>
            )}
          </Button>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFile}
            data-testid={`photo-input-${slot}`}
          />
        </div>
      </div>
    </div>
  );
}
